import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, API } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { 
  Navigation2, 
  MapPin, 
  Locate,
  Route,
  Clock,
  Footprints,
  ChevronUp,
  ChevronDown,
  X,
  Loader2,
  ArrowUp,
  ArrowLeft,
  ArrowRight,
  Circle,
  Target,
  Car,
  Volume2,
  VolumeX,
  Settings2,
  Camera,
  Euro,
  Zap,
  Search,
  Plus,
  Minus,
  ArrowLeftCircle,
  Home
} from "lucide-react";
import { ShareLogo } from "../components/ShareLogo";

// Fix Leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Custom icons
const userIcon = new L.DivIcon({
  html: `<div style="background: #FF3366; width: 18px; height: 18px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4);"></div>`,
  className: '',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const startIcon = new L.DivIcon({
  html: `<div style="background: #22C55E; width: 14px; height: 14px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);"></div>`,
  className: '',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const endIcon = new L.DivIcon({
  html: `<div style="background: #FF3366; width: 28px; height: 28px; border-radius: 8px; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg>
  </div>`,
  className: '',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
});

const radarIcon = new L.DivIcon({
  html: `<div style="background: #FF6600; width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4" stroke="white" stroke-width="2"/></svg>
  </div>`,
  className: '',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

// Map controller
const MapController = ({ routeCoords, startCoords, endCoords, userLocation }) => {
  const map = useMap();
  
  useEffect(() => {
    if (routeCoords?.length > 0) {
      const bounds = L.latLngBounds(routeCoords);
      map.fitBounds(bounds, { padding: [50, 50] });
    } else if (userLocation) {
      map.setView(userLocation, 18); // Street level zoom
    }
  }, [routeCoords, startCoords, endCoords, userLocation, map]);
  
  return null;
};

// Decode polyline (for Mapbox/OSRM encoded polylines)
const decodePolyline = (encoded) => {
  const coords = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let b;
    let shift = 0;
    let result = 0;

    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    const dlat = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lat += dlat;

    shift = 0;
    result = 0;

    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    const dlng = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lng += dlng;

    coords.push([lat / 1e5, lng / 1e5]);
  }

  return coords;
};

// Zoom tracker component
const ZoomTracker = ({ onZoomChange }) => {
  const map = useMap();
  
  useEffect(() => {
    const handleZoom = () => {
      onZoomChange(map.getZoom());
    };
    
    map.on('zoomend', handleZoom);
    return () => map.off('zoomend', handleZoom);
  }, [map, onZoomChange]);
  
  return null;
};

// Translations
const T = {
  fr: {
    search: "Où allez-vous ?",
    from: "Point de départ",
    to: "Destination",
    go: "C'est parti !",
    stop: "Arrêter",
    car: "Voiture",
    pedestrian: "À pied",
    avoidTolls: "Sans péage",
    avoidHighways: "Sans autoroute",
    radars: "Radars",
    myPosition: "Ma position",
    calculating: "Calcul...",
    routeReady: "Itinéraire prêt",
    eta: "Arrivée",
    via: "via"
  },
  en: {
    search: "Where to?",
    from: "Starting point",
    to: "Destination", 
    go: "Let's go!",
    stop: "Stop",
    car: "Drive",
    pedestrian: "Walk",
    avoidTolls: "No tolls",
    avoidHighways: "No highways",
    radars: "Cameras",
    myPosition: "My location",
    calculating: "Calculating...",
    routeReady: "Route ready",
    eta: "ETA",
    via: "via"
  }
};

// Sample radars
const SAMPLE_RADARS = [
  { id: 1, lat: 48.8566, lng: 2.3522, speed: 50 },
  { id: 2, lat: 48.8698, lng: 2.3078, speed: 50 },
  { id: 3, lat: 43.6047, lng: 1.4442, speed: 70 },
  { id: 4, lat: 44.8378, lng: -0.5792, speed: 80 },
  { id: 5, lat: 45.764, lng: 4.8357, speed: 90 },
];

const GPSPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState([46.603354, 1.888334]);
  const [mapZoom, setMapZoom] = useState(6);
  const [isZoomedOut, setIsZoomedOut] = useState(false);
  const [showRecenterBtn, setShowRecenterBtn] = useState(false);
  
  // Navigation Mode
  const [is3DMode, setIs3DMode] = useState(false); // Navigation mode (street level)
  const mapContainerRef = useRef(null);
  
  // UI State
  const [panelOpen, setPanelOpen] = useState(true);
  const [panelView, setPanelView] = useState('search');
  const [showSettings, setShowSettings] = useState(false);
  
  // Inputs
  const [startAddress, setStartAddress] = useState("");
  const [endAddress, setEndAddress] = useState("");
  const [startCoords, setStartCoords] = useState(null);
  const [endCoords, setEndCoords] = useState(null);
  
  // Autocomplete
  const [startSuggestions, setStartSuggestions] = useState([]);
  const [endSuggestions, setEndSuggestions] = useState([]);
  const [showStartSuggestions, setShowStartSuggestions] = useState(false);
  const [showEndSuggestions, setShowEndSuggestions] = useState(false);
  const [searchingStart, setSearchingStart] = useState(false);
  const [searchingEnd, setSearchingEnd] = useState(false);
  const startInputRef = useRef(null);
  const endInputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  
  // Settings
  const [transportMode, setTransportMode] = useState('car');
  const [avoidTolls, setAvoidTolls] = useState(false);
  const [avoidHighways, setAvoidHighways] = useState(false);
  const [language, setLanguage] = useState('fr');
  const [audioMode, setAudioMode] = useState('active');
  const [radarAlerts, setRadarAlerts] = useState(true);
  
  // Route
  const [route, setRoute] = useState(null);
  const [routeCoords, setRouteCoords] = useState([]);
  const [instructions, setInstructions] = useState([]);
  const [radarsOnRoute, setRadarsOnRoute] = useState([]);
  const [isNavigating, setIsNavigating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  
  const synthRef = useRef(window.speechSynthesis);
  const mapRef = useRef(null);
  const t = T[language] || T.fr;

  // Get location on mount - Zoom to street level like Waze
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = [pos.coords.latitude, pos.coords.longitude];
          setUserLocation(loc);
          setMapCenter(loc);
          setMapZoom(18); // Street level zoom like Waze
          setShowRecenterBtn(false);
        },
        () => {
          setMapCenter([48.8566, 2.3522]);
          setMapZoom(12);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, []);

  // Track zoom changes to show/hide recenter button
  const handleZoomChange = useCallback((newZoom) => {
    if (userLocation) {
      // Show recenter button if zoomed out below street level (< 16)
      if (newZoom < 16) {
        setShowRecenterBtn(true);
        setIsZoomedOut(true);
      } else {
        setIsZoomedOut(false);
      }
    }
  }, [userLocation]);

  // Recenter map to user position with street level zoom
  const recenterToPosition = useCallback(() => {
    if (userLocation && mapRef.current) {
      mapRef.current.setView(userLocation, 18, { animate: true, duration: 0.5 });
      setShowRecenterBtn(false);
      setIsZoomedOut(false);
      toast.success("📍 Position recentrée");
    }
  }, [userLocation]);

  // ==================== NAVIGATION MODE ====================
  
  // Toggle navigation mode (zoom to street level)
  const toggleNavigationMode = useCallback(() => {
    setIs3DMode(prev => {
      const newMode = !prev;
      if (newMode) {
        // Zoom to street level for navigation
        setMapZoom(18);
        if (userLocation && mapRef.current) {
          mapRef.current.setView(userLocation, 18, { animate: true });
        }
        toast.success("🚗 Mode navigation activé");
      } else {
        // Return to overview
        if (mapRef.current) {
          mapRef.current.setZoom(15);
        }
        toast.info("Vue normale");
      }
      return newMode;
    });
  }, [userLocation]);

  // Speech
  const speak = useCallback((text) => {
    if (audioMode === 'mute') return;
    synthRef.current.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language === 'fr' ? 'fr-FR' : 'en-GB';
    synthRef.current.speak(utterance);
  }, [audioMode, language]);

  // Geocode - Try backend API first, fallback to Nominatim
  const geocode = async (address) => {
    // Try backend API first (Mapbox or OSRM)
    if (token) {
      try {
        const res = await axios.get(`${API}/routing/geocode`, {
          params: { address, country: 'fr' },
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000
        });
        if (res.data) {
          return { lat: res.data.lat, lng: res.data.lng, name: res.data.address };
        }
      } catch (error) {
        console.warn("Backend geocoding failed, falling back to Nominatim:", error.message);
      }
    }
    
    // Fallback to Nominatim
    try {
      const res = await axios.get(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
        { headers: { 'Accept-Language': language } }
      );
      if (res.data?.length) {
        return { lat: parseFloat(res.data[0].lat), lng: parseFloat(res.data[0].lon), name: res.data[0].display_name };
      }
    } catch (error) {
      console.warn("Geocoding failed:", error.message);
    }
    return null;
  };

  // Autocomplete search
  const searchAddresses = async (query, isStart) => {
    if (!query || query.length < 2) {
      if (isStart) {
        setStartSuggestions([]);
        setShowStartSuggestions(false);
      } else {
        setEndSuggestions([]);
        setShowEndSuggestions(false);
      }
      return;
    }

    if (isStart) setSearchingStart(true);
    else setSearchingEnd(true);

    try {
      const res = await axios.get(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&countrycodes=fr,be,ch,lu&addressdetails=1`,
        { headers: { 'Accept-Language': language } }
      );
      
      const suggestions = res.data.map(item => ({
        id: item.place_id,
        name: item.display_name,
        shortName: formatShortAddress(item),
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon),
        type: item.type
      }));

      if (isStart) {
        setStartSuggestions(suggestions);
        setShowStartSuggestions(suggestions.length > 0);
      } else {
        setEndSuggestions(suggestions);
        setShowEndSuggestions(suggestions.length > 0);
      }
    } catch (e) {
      console.error('Autocomplete error:', e);
    } finally {
      if (isStart) setSearchingStart(false);
      else setSearchingEnd(false);
    }
  };

  // Format short address for display
  const formatShortAddress = (item) => {
    const parts = [];
    if (item.address) {
      if (item.address.road) parts.push(item.address.road);
      if (item.address.city || item.address.town || item.address.village) {
        parts.push(item.address.city || item.address.town || item.address.village);
      }
      if (item.address.postcode) parts.push(item.address.postcode);
    }
    return parts.length > 0 ? parts.join(', ') : item.display_name.split(',').slice(0, 2).join(',');
  };

  // Debounced search
  const handleAddressChange = (value, isStart) => {
    if (isStart) {
      setStartAddress(value);
      setStartCoords(null);
    } else {
      setEndAddress(value);
    }

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced search
    debounceTimerRef.current = setTimeout(() => {
      searchAddresses(value, isStart);
    }, 300);
  };

  // Select suggestion
  const selectSuggestion = (suggestion, isStart) => {
    if (isStart) {
      setStartAddress(suggestion.shortName);
      setStartCoords({ lat: suggestion.lat, lng: suggestion.lng });
      setStartSuggestions([]);
      setShowStartSuggestions(false);
    } else {
      setEndAddress(suggestion.shortName);
      setEndCoords({ lat: suggestion.lat, lng: suggestion.lng });
      setEndSuggestions([]);
      setShowEndSuggestions(false);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (startInputRef.current && !startInputRef.current.contains(e.target)) {
        setShowStartSuggestions(false);
      }
      if (endInputRef.current && !endInputRef.current.contains(e.target)) {
        setShowEndSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Use my location with reverse geocoding
  const useMyLocation = async () => {
    if (!userLocation) {
      toast.error("Position GPS non disponible. Activez la géolocalisation.");
      return;
    }
    
    setSearchingStart(true);
    setStartCoords({ lat: userLocation[0], lng: userLocation[1] });
    
    // Try backend API first for reverse geocoding
    if (token) {
      try {
        const res = await axios.get(`${API}/routing/reverse-geocode`, {
          params: { lat: userLocation[0], lng: userLocation[1] },
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000
        });
        
        if (res.data?.address) {
          // Extract short address from full address
          const fullAddr = res.data.address;
          const parts = fullAddr.split(',');
          const shortAddr = parts.slice(0, 2).join(',').trim();
          setStartAddress(shortAddr || t.myPosition);
          toast.success("📍 Position actuelle détectée");
          setSearchingStart(false);
          return;
        }
      } catch (error) {
        console.warn("Backend reverse geocoding failed:", error.message);
      }
    }
    
    // Fallback to Nominatim
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const res = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${userLocation[0]}&lon=${userLocation[1]}&zoom=18&addressdetails=1`,
        { 
          headers: { 'Accept-Language': language },
          signal: controller.signal
        }
      );
      clearTimeout(timeoutId);
      
      if (res.data?.address) {
        const addr = res.data.address;
        const shortAddr = [
          addr.house_number,
          addr.road || addr.pedestrian || addr.street,
          addr.city || addr.town || addr.village || addr.municipality
        ].filter(Boolean).join(' ');
        
        setStartAddress(shortAddr || t.myPosition);
        toast.success("📍 Position actuelle détectée");
      } else {
        setStartAddress(t.myPosition);
        toast.success("📍 Position GPS utilisée");
      }
    } catch (error) {
      console.warn("Reverse geocoding failed:", error.message);
      setStartAddress(t.myPosition);
      if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
        toast.info("📍 Position GPS utilisée (géocodage lent)");
      } else {
        toast.success("📍 Position GPS utilisée");
      }
    } finally {
      setSearchingStart(false);
    }
  };

  // Get route - Try backend API first, fallback to OSRM public
  const getRoute = async () => {
    if (!endAddress) { toast.error("Entrez une destination"); return; }
    
    setLoading(true);
    setPanelView('search');
    
    try {
      let start = startCoords;
      if (!start && startAddress) {
        start = await geocode(startAddress);
      }
      if (!start && userLocation) {
        start = { lat: userLocation[0], lng: userLocation[1] };
        setStartAddress(t.myPosition);
      }
      if (!start) { toast.error("Position introuvable"); setLoading(false); return; }
      
      const end = await geocode(endAddress);
      if (!end) { toast.error("Destination introuvable"); setLoading(false); return; }
      
      setStartCoords(start);
      setEndCoords(end);
      
      const mode = transportMode === 'pieton' ? 'walking' : 'driving';
      let routeData = null;
      let coords = [];
      let steps = [];
      
      // Try backend API first (Mapbox or OSRM)
      if (token) {
        try {
          const res = await axios.get(`${API}/routing/route`, {
            params: {
              start_lat: start.lat,
              start_lng: start.lng,
              end_lat: end.lat,
              end_lng: end.lng,
              mode
            },
            headers: { Authorization: `Bearer ${token}` },
            timeout: 15000
          });
          
          if (res.data) {
            routeData = res.data;
            // Decode polyline if needed or use coordinates
            if (res.data.geometry) {
              // Decode encoded polyline
              coords = decodePolyline(res.data.geometry);
            }
            steps = (res.data.steps || []).map((s, i) => ({
              index: i,
              type: 'instruction',
              instruction: s.instruction,
              name: s.name || "",
              distance: s.distance,
              duration: s.duration
            }));
          }
        } catch (error) {
          console.warn("Backend routing failed, falling back to OSRM:", error.message);
        }
      }
      
      // Fallback to public OSRM if backend failed
      if (!routeData || coords.length === 0) {
        const profile = transportMode === 'pieton' ? 'foot' : 'car';
        const res = await axios.get(
          `https://router.project-osrm.org/route/v1/${profile}/${start.lng},${start.lat};${end.lng},${end.lat}?overview=full&geometries=geojson&steps=true`
        );
        
        if (!res.data?.routes?.[0]) { toast.error("Itinéraire introuvable"); setLoading(false); return; }
        
        routeData = res.data.routes[0];
        coords = routeData.geometry.coordinates.map(c => [c[1], c[0]]);
        steps = routeData.legs[0].steps.map((s, i) => ({
          index: i,
          type: s.maneuver.type,
          modifier: s.maneuver.modifier,
          name: s.name || "",
          distance: s.distance,
          duration: s.duration
        }));
      }
      
      setRoute(routeData);
      setRouteCoords(coords);
      setInstructions(steps);
      
      // Radars
      if (radarAlerts) {
        const radars = SAMPLE_RADARS.filter(r => {
          return coords.some(c => {
            const d = Math.sqrt(Math.pow(c[0] - r.lat, 2) + Math.pow(c[1] - r.lng, 2)) * 111;
            return d < 0.5;
          });
        });
        setRadarsOnRoute(radars);
      }
      
      setPanelView('route');
      toast.success(t.routeReady);
      
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setLoading(false);
    }
  };

  // Start navigation
  const startNavigation = () => {
    setIsNavigating(true);
    setPanelView('navigation');
    speak(language === 'fr' ? "Navigation démarrée" : "Navigation started");
  };

  // Stop navigation
  const stopNavigation = () => {
    setIsNavigating(false);
    setPanelView('route');
    synthRef.current.cancel();
  };

  // Clear
  const clearRoute = () => {
    setRoute(null);
    setRouteCoords([]);
    setInstructions([]);
    setRadarsOnRoute([]);
    setEndAddress("");
    setEndCoords(null);
    setPanelView('search');
    setIsNavigating(false);
  };

  // Format
  const formatDist = (m) => m >= 1000 ? `${(m/1000).toFixed(1)} km` : `${Math.round(m)} m`;
  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    return h > 0 ? `${h}h${m.toString().padStart(2, '0')}` : `${m} min`;
  };
  const getETA = (s) => {
    const eta = new Date(Date.now() + s * 1000);
    return eta.toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  // Get instruction icon
  const getIcon = (type, modifier) => {
    if (type === 'turn' && modifier?.includes('left')) return <ArrowLeft size={24} />;
    if (type === 'turn' && modifier?.includes('right')) return <ArrowRight size={24} />;
    if (type === 'arrive') return <MapPin size={24} />;
    return <ArrowUp size={24} />;
  };

  const getInstructionText = (type, modifier, name) => {
    let text = "";
    if (type === 'depart') text = language === 'fr' ? "Départ" : "Depart";
    else if (type === 'arrive') text = language === 'fr' ? "Arrivée" : "Arrive";
    else if (type === 'turn') {
      if (modifier?.includes('left')) text = language === 'fr' ? "Tournez à gauche" : "Turn left";
      else if (modifier?.includes('right')) text = language === 'fr' ? "Tournez à droite" : "Turn right";
      else text = language === 'fr' ? "Continuez" : "Continue";
    }
    else text = language === 'fr' ? "Continuez" : "Continue";
    return text;
  };

  return (
    <div className="h-[100dvh] w-full flex flex-col bg-slate-100 overflow-hidden">
      {/* Top Bar - Mobile App Style */}
      <div className="absolute top-0 left-0 right-0 z-[1000] safe-area-top">
        <div className="flex items-center justify-between p-3 bg-gradient-to-b from-black/30 to-transparent">
          {/* Back button */}
          <button 
            onClick={() => navigate('/dashboard')}
            className="w-10 h-10 rounded-full bg-white/90 backdrop-blur shadow-lg flex items-center justify-center text-slate-700"
          >
            <ArrowLeftCircle size={22} />
          </button>
          
          {/* Center - Logo or Navigation info */}
          {isNavigating ? (
            <div className="flex-1 mx-3 bg-white/90 backdrop-blur rounded-2xl px-4 py-2 shadow-lg">
              <div className="flex items-center justify-center gap-3">
                <div className="text-lg font-bold text-slate-800">{formatTime(route?.duration || 0)}</div>
                <div className="w-1 h-1 rounded-full bg-slate-400" />
                <div className="text-sm text-slate-500">{formatDist(route?.distance || 0)}</div>
                <div className="w-1 h-1 rounded-full bg-slate-400" />
                <div className="text-sm font-medium text-[#FF3366]">{getETA(route?.duration || 0)}</div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 bg-white/90 backdrop-blur rounded-full px-3 py-1.5 shadow-lg">
              <ShareLogo size={24} />
              <span className="font-bold text-slate-800 text-sm" style={{ fontFamily: 'Outfit' }}>GPS</span>
            </div>
          )}
          
          {/* Right buttons */}
          <div className="flex gap-2">
            <button 
              onClick={() => setAudioMode(audioMode === 'active' ? 'mute' : 'active')}
              className={`w-10 h-10 rounded-full shadow-lg flex items-center justify-center ${
                audioMode === 'active' ? 'bg-white/90' : 'bg-slate-200/90'
              }`}
            >
              {audioMode === 'active' ? <Volume2 size={18} className="text-[#FF3366]" /> : <VolumeX size={18} className="text-slate-500" />}
            </button>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative overflow-hidden">
        {/* Map Container - No transforms, keeps full interactivity */}
        <div 
          ref={mapContainerRef}
          className="w-full h-full"
        >
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: "100%", width: "100%" }}
            zoomControl={false}
            ref={mapRef}
          >
            <TileLayer
              attribution='&copy; OSM'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapController routeCoords={routeCoords} startCoords={startCoords} endCoords={endCoords} userLocation={userLocation} />
            <ZoomTracker onZoomChange={handleZoomChange} />
            
            {userLocation && <Marker position={userLocation} icon={userIcon} />}
            {startCoords && <Marker position={[startCoords.lat, startCoords.lng]} icon={startIcon} />}
            {endCoords && <Marker position={[endCoords.lat, endCoords.lng]} icon={endIcon} />}
            
            {routeCoords.length > 0 && (
              <Polyline positions={routeCoords} color="#FF3366" weight={6} opacity={0.9} />
            )}
            
            {radarAlerts && radarsOnRoute.map(r => (
              <Marker key={r.id} position={[r.lat, r.lng]} icon={radarIcon}>
                <Popup>Radar {r.speed} km/h</Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* 3D Perspective Overlay - Visual effect only, doesn't block interactions */}
        {is3DMode && (
          <div 
            className="absolute inset-0 pointer-events-none z-[500]"
            style={{
              background: 'linear-gradient(to bottom, rgba(135,206,235,0.3) 0%, transparent 30%, transparent 100%)',
            }}
          />
        )}

        {/* Navigation Mode Toggle Button */}
        <button
          onClick={toggleNavigationMode}
          className={`absolute left-3 top-3 z-[1000] px-4 py-2 rounded-xl shadow-lg font-semibold text-sm transition-all ${
            is3DMode 
              ? 'bg-[#FF3366] text-white' 
              : 'bg-white text-slate-700 hover:bg-slate-50'
          }`}
          data-testid="toggle-3d-btn"
        >
          {is3DMode ? '🚗 Navigation' : '🗺️ Carte'}
        </button>

        {/* Map Controls - Right side */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 z-[1000] flex flex-col gap-2">
          <button
            onClick={() => {
              const newZoom = (mapRef.current?.getZoom() || 10) + 1;
              mapRef.current?.setZoom(newZoom);
              handleZoomChange(newZoom);
            }}
            className="w-11 h-11 bg-white rounded-xl shadow-lg flex items-center justify-center text-slate-600 active:bg-slate-100"
            data-testid="map-zoom-in"
          >
            <Plus size={22} />
          </button>
          <button
            onClick={() => {
              const newZoom = (mapRef.current?.getZoom() || 10) - 1;
              mapRef.current?.setZoom(newZoom);
              handleZoomChange(newZoom);
            }}
            className="w-11 h-11 bg-white rounded-xl shadow-lg flex items-center justify-center text-slate-600 active:bg-slate-100"
            data-testid="map-zoom-out"
          >
            <Minus size={22} />
          </button>
        </div>

        {/* Recenter Button - Shows when zoomed out */}
        {showRecenterBtn && userLocation && (
          <button
            onClick={recenterToPosition}
            className="absolute left-3 bottom-[320px] z-[1000] flex items-center gap-2 px-4 py-3 bg-[#FF3366] text-white rounded-xl shadow-2xl hover:bg-[#E62E5C] active:scale-95 transition-all animate-bounce-subtle"
            data-testid="recenter-btn"
          >
            <Target size={20} className="animate-pulse" />
            <span className="font-semibold text-sm">Ma position</span>
          </button>
        )}

        {/* Quick recenter button - Always visible in bottom right */}
        <button
          onClick={recenterToPosition}
          className={`absolute right-3 bottom-[320px] z-[1000] w-12 h-12 rounded-xl shadow-lg flex items-center justify-center transition-all ${
            isZoomedOut 
              ? 'bg-[#FF3366] text-white animate-pulse' 
              : 'bg-white text-slate-600 hover:bg-slate-50'
          }`}
          data-testid="quick-recenter-btn"
        >
          <Navigation2 size={22} className={isZoomedOut ? 'text-white' : 'text-[#FF3366]'} />
        </button>

        {/* Navigation Overlay - Current instruction */}
        {isNavigating && instructions.length > 0 && (
          <div className="absolute top-20 left-3 right-3 z-[1000]">
            <div className="bg-[#FF3366] text-white rounded-2xl p-4 shadow-2xl">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                  {getIcon(instructions[currentStep]?.type, instructions[currentStep]?.modifier)}
                </div>
                <div className="flex-1">
                  <div className="text-xl font-bold">
                    {getInstructionText(instructions[currentStep]?.type, instructions[currentStep]?.modifier)}
                  </div>
                  <div className="text-white/80 text-sm">
                    {instructions[currentStep]?.name || formatDist(instructions[currentStep]?.distance || 0)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Panel - Swipeable */}
      <div 
        className={`absolute bottom-0 left-0 right-0 z-[1000] transition-transform duration-300 ease-out ${
          panelOpen ? 'translate-y-0' : 'translate-y-[calc(100%-60px)]'
        }`}
      >
        {/* Handle */}
        <div 
          className="flex justify-center py-2 cursor-pointer"
          onClick={() => setPanelOpen(!panelOpen)}
        >
          <div className="w-10 h-1 rounded-full bg-slate-300" />
        </div>

        <div className="bg-white rounded-t-3xl shadow-2xl safe-area-bottom overflow-hidden">
          {/* Panel Header */}
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <button onClick={() => setPanelOpen(!panelOpen)} className="text-slate-400">
              {panelOpen ? <ChevronDown size={24} /> : <ChevronUp size={24} />}
            </button>
            
            {/* Mode selector - Always visible */}
            <div className="flex bg-slate-100 rounded-full p-1">
              <button
                onClick={() => setTransportMode('car')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  transportMode === 'car' ? 'bg-[#FF3366] text-white shadow' : 'text-slate-600'
                }`}
              >
                <Car size={18} />
                <span className="hidden sm:inline">{t.car}</span>
              </button>
              <button
                onClick={() => setTransportMode('pieton')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  transportMode === 'pieton' ? 'bg-[#FF3366] text-white shadow' : 'text-slate-600'
                }`}
              >
                <Footprints size={18} />
                <span className="hidden sm:inline">{t.pedestrian}</span>
              </button>
            </div>

            <button 
              onClick={() => setShowSettings(!showSettings)}
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                showSettings ? 'bg-[#FF3366] text-white' : 'text-slate-400'
              }`}
            >
              <Settings2 size={20} />
            </button>
          </div>

          {/* Settings */}
          {showSettings && (
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
              <div className="flex flex-wrap gap-2">
                {transportMode === 'car' && (
                  <>
                    <button
                      onClick={() => setAvoidTolls(!avoidTolls)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${
                        avoidTolls ? 'bg-[#FF3366] text-white' : 'bg-white border border-slate-200 text-slate-600'
                      }`}
                    >
                      <Euro size={14} /> {t.avoidTolls}
                    </button>
                    <button
                      onClick={() => setAvoidHighways(!avoidHighways)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${
                        avoidHighways ? 'bg-[#FF3366] text-white' : 'bg-white border border-slate-200 text-slate-600'
                      }`}
                    >
                      <Zap size={14} /> {t.avoidHighways}
                    </button>
                  </>
                )}
                <button
                  onClick={() => setRadarAlerts(!radarAlerts)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${
                    radarAlerts ? 'bg-orange-500 text-white' : 'bg-white border border-slate-200 text-slate-600'
                  }`}
                >
                  <Camera size={14} /> {t.radars}
                </button>
                <button
                  onClick={() => setLanguage(language === 'fr' ? 'en' : 'fr')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-white border border-slate-200 text-slate-600"
                >
                  {language === 'fr' ? '🇫🇷' : '🇬🇧'}
                </button>
              </div>
            </div>
          )}

          {/* Search View */}
          {panelView === 'search' && (
            <div className="p-4 space-y-3">
              {/* From */}
              <div className="relative" ref={startInputRef}>
                <div className="absolute left-4 top-4 w-3 h-3 rounded-full bg-green-500 z-10" />
                <input
                  type="text"
                  placeholder={t.from}
                  value={startAddress}
                  onChange={(e) => handleAddressChange(e.target.value, true)}
                  onFocus={() => startSuggestions.length > 0 && setShowStartSuggestions(true)}
                  className="w-full pl-10 pr-24 py-4 rounded-2xl bg-slate-100 text-base focus:bg-white focus:ring-2 focus:ring-[#FF3366]/30 outline-none transition-all"
                />
                {/* Ma Position Button */}
                <button
                  data-testid="gps-my-location-btn"
                  onClick={useMyLocation}
                  disabled={searchingStart || !userLocation}
                  className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-3 py-2 rounded-xl bg-green-500 text-white text-sm font-medium hover:bg-green-600 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  title={!userLocation ? "Activez la géolocalisation" : "Utiliser ma position"}
                >
                  {searchingStart ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <>
                      <Locate size={16} />
                      <span className="hidden sm:inline">GPS</span>
                    </>
                  )}
                </button>
                
                {/* Start Suggestions Dropdown */}
                {showStartSuggestions && startSuggestions.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden z-50 max-h-[200px] overflow-y-auto">
                    {startSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.id}
                        onClick={() => selectSuggestion(suggestion, true)}
                        className="w-full px-4 py-3 flex items-start gap-3 hover:bg-slate-50 active:bg-slate-100 text-left border-b border-slate-50 last:border-0"
                      >
                        <MapPin size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">{suggestion.shortName}</div>
                          <div className="text-xs text-slate-400 truncate">{suggestion.name}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* To */}
              <div className="relative" ref={endInputRef}>
                <div className="absolute left-4 top-4 w-3 h-3 rounded-full bg-[#FF3366] z-10" />
                <input
                  type="text"
                  placeholder={t.search}
                  value={endAddress}
                  onChange={(e) => handleAddressChange(e.target.value, false)}
                  onFocus={() => endSuggestions.length > 0 && setShowEndSuggestions(true)}
                  className="w-full pl-10 pr-4 py-4 rounded-2xl bg-slate-100 text-base focus:bg-white focus:ring-2 focus:ring-[#FF3366]/30 outline-none transition-all"
                />
                {searchingEnd && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <Loader2 size={20} className="animate-spin text-slate-400" />
                  </div>
                )}
                
                {/* End Suggestions Dropdown */}
                {showEndSuggestions && endSuggestions.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden z-50 max-h-[200px] overflow-y-auto">
                    {endSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.id}
                        onClick={() => selectSuggestion(suggestion, false)}
                        className="w-full px-4 py-3 flex items-start gap-3 hover:bg-slate-50 active:bg-slate-100 text-left border-b border-slate-50 last:border-0"
                      >
                        <MapPin size={18} className="text-[#FF3366] mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">{suggestion.shortName}</div>
                          <div className="text-xs text-slate-400 truncate">{suggestion.name}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Search button */}
              <button
                onClick={getRoute}
                disabled={loading}
                className="w-full py-4 rounded-2xl bg-[#FF3366] text-white font-bold text-lg flex items-center justify-center gap-2 active:bg-[#E62E5C] disabled:opacity-50"
              >
                {loading ? (
                  <><Loader2 className="animate-spin" size={22} /> {t.calculating}</>
                ) : (
                  <><Route size={22} /> {language === 'fr' ? 'Rechercher' : 'Search'}</>
                )}
              </button>
            </div>
          )}

          {/* Route View */}
          {panelView === 'route' && route && (
            <div className="p-4 space-y-4">
              {/* Route summary */}
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                  transportMode === 'car' ? 'bg-[#FF3366]/10' : 'bg-blue-100'
                }`}>
                  {transportMode === 'car' ? <Car size={28} className="text-[#FF3366]" /> : <Footprints size={28} className="text-blue-500" />}
                </div>
                <div className="flex-1">
                  <div className="text-2xl font-bold text-slate-800">{formatTime(route.duration)}</div>
                  <div className="text-slate-500">{formatDist(route.distance)} • {t.eta} {getETA(route.duration)}</div>
                </div>
                {radarsOnRoute.length > 0 && (
                  <div className="px-3 py-1.5 rounded-xl bg-orange-100 text-orange-600 text-sm font-bold flex items-center gap-1">
                    <Camera size={16} /> {radarsOnRoute.length}
                  </div>
                )}
              </div>

              {/* Quick steps preview */}
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
                {instructions.slice(0, 5).map((step, i) => (
                  <div key={i} className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-xl">
                    <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center text-slate-600">
                      {getIcon(step.type, step.modifier)}
                    </div>
                    <span className="text-sm text-slate-600 whitespace-nowrap">{formatDist(step.distance)}</span>
                  </div>
                ))}
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={clearRoute}
                  className="flex-1 py-4 rounded-2xl bg-slate-100 text-slate-600 font-bold flex items-center justify-center gap-2"
                >
                  <X size={20} /> {language === 'fr' ? 'Annuler' : 'Cancel'}
                </button>
                <button
                  onClick={startNavigation}
                  className="flex-[2] py-4 rounded-2xl bg-green-500 text-white font-bold text-lg flex items-center justify-center gap-2 active:bg-green-600"
                >
                  <Navigation2 size={22} /> {t.go}
                </button>
              </div>
            </div>
          )}

          {/* Navigation View */}
          {panelView === 'navigation' && (
            <div className="p-4 space-y-4">
              {/* Next steps */}
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {instructions.slice(currentStep, currentStep + 4).map((step, i) => (
                  <div key={step.index} className={`flex items-center gap-3 p-3 rounded-xl ${
                    i === 0 ? 'bg-[#FF3366]/10 border border-[#FF3366]' : 'bg-slate-50'
                  }`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      i === 0 ? 'bg-[#FF3366] text-white' : 'bg-slate-200 text-slate-500'
                    }`}>
                      {getIcon(step.type, step.modifier)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{getInstructionText(step.type, step.modifier)}</div>
                      {step.name && <div className="text-sm text-slate-500 truncate">{step.name}</div>}
                    </div>
                    <div className="text-sm text-slate-400">{formatDist(step.distance)}</div>
                  </div>
                ))}
              </div>

              {/* Stop button */}
              <button
                onClick={stopNavigation}
                className="w-full py-4 rounded-2xl bg-red-500 text-white font-bold flex items-center justify-center gap-2 active:bg-red-600"
              >
                <X size={22} /> {t.stop}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GPSPage;
